import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatToDayMonthYear, formatTimestamp } from "../../utils/TextUtils.js";

function DebtRepayments() {
    const [repayments, setRepayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterDate, setFilterDate] = useState(""); // Filter by specific date
    const [filterMonth, setFilterMonth] = useState(""); // Filter by month and year
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [repaymentToCancel, setRepaymentToCancel] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    
    const perPage = 10;
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");
    const navigate = useNavigate();

    const canAdd = (userRole) => {
        return allowedToView(userRole, ['admin', 'superadmin', 'user']);
    };

    const canCancel = (userRole) => {
        return allowedToView(userRole, ['admin', 'superadmin']);
    };

    // Memoize fetchRepayments to prevent unnecessary re-renders
    const fetchRepayments = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/debt-repayments?page=${currentPage}&limit=${perPage}`;
            
            if (searchTerm) {
                url += `&search=${searchTerm}`;
            }
            if (filterDate) {
                url += `&start_date=${filterDate}&end_date=${filterDate}`;
            }
            if (filterMonth) {
                const [year, month] = filterMonth.split('-');
                const startDate = `${year}-${month}-01`;
                const endDate = new Date(year, month, 0).toISOString().split('T')[0];
                url += `&start_date=${startDate}&end_date=${endDate}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setRepayments(data.data || []);
                setTotalPages(data.meta?.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch debt repayments");
            }
        } catch (error) {
            setError("An error occurred while fetching debt repayments.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, filterDate, filterMonth, navigate, token]);

    // Fetch repayments when dependencies change
    useEffect(() => {
        fetchRepayments();
    }, [fetchRepayments]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null);
        } else {
            setIsDropdownOpen(index);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterDateChange = (event) => {
        setFilterDate(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterMonthChange = (event) => {
        setFilterMonth(event.target.value);
        setCurrentPage(1);
    };

    // Handle cancel repayment
    const handleCancelRepayment = (repayment) => {
        setRepaymentToCancel(repayment);
        setIsConfirmDialogOpen(true);
    };

    const confirmCancelRepayment = async () => {
        if (!repaymentToCancel) return;

        setIsCancelling(true);
        try {
            const response = await fetch(`${API_URL}/debt-repayments/${repaymentToCancel.id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                setNotification({
                    type: "success",
                    message: "Repayment cancelled successfully."
                });
                fetchRepayments(); // Refresh the list
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setNotification({
                    type: "danger",
                    message: errorData.message || "Failed to cancel repayment."
                });
            }
        } catch (error) {
            setNotification({
                type: "danger",
                message: "An error occurred while cancelling the repayment."
            });
            console.error("Error:", error);
        } finally {
            setIsCancelling(false);
            setIsConfirmDialogOpen(false);
            setRepaymentToCancel(null);
        }
    };

    const cancelDeleteRepayment = () => {
        setIsConfirmDialogOpen(false);
        setRepaymentToCancel(null);
    };

    // Get status tag
    const getStatusTag = (status) => {
        switch (status) {
            case 'completed':
                return { className: 'is-success', text: 'Completed' };
            case 'pending':
                return { className: 'is-warning', text: 'Pending' };
            case 'cancelled':
                return { className: 'is-danger', text: 'Cancelled' };
            default:
                return { className: '', text: status };
        }
    };

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Debt Repayments</p>
                        </div>
                    </div>
                    <div className="level-right">
                        {canAdd(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New Repayment
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                <h1>Debt Repayment List</h1>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Confirmation Dialog */}
                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-danger"
                    title="Cancel Repayment"
                    message={`Are you sure you want to cancel repayment of KES ${repaymentToCancel?.amount_paid} made by ${repaymentToCancel?.farmer?.first_name} ${repaymentToCancel?.farmer?.last_name}? This action cannot be undone.`}
                    onConfirm={confirmCancelRepayment}
                    onCancel={cancelDeleteRepayment}
                    isLoading={isCancelling}
                />

                {/* Filters */}
                <div className="field is-horizontal">
                    <div className="field-body">
                        <div className="field">
                            <label className="label">Search by Farmer Name or Number</label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Search by farmer name or farmer number"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <div className="control">
                                <input
                                    type="date"
                                    className="input"
                                    value={filterDate}
                                    onChange={handleFilterDateChange}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <div className="control">
                                <input
                                    type="month"
                                    className="input"
                                    value={filterMonth}
                                    onChange={handleFilterMonthChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <p>Loading debt repayments...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Farmer</th>
                                    <th>Amount Paid</th>
                                    <th>Payment Date</th>
                                    <th>Received By</th>
                                    <th>Created At</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {repayments.length > 0 ? (
                                    repayments.map((repayment, index) => (
                                        <tr key={repayment.id}>
                                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                                            <td>
                                                <Link to={`/dashboard/farmers/${repayment?.farmer?.id}/details`} className="primary-text-color">
                                                    {repayment?.farmer?.first_name} {repayment?.farmer?.last_name}
                                                    {repayment?.farmer?.farmer_number && ` (${repayment?.farmer?.farmer_number})`}
                                                </Link>
                                            </td>
                                            <td className="has-text-weight-bold">
                                                KES {parseFloat(repayment.amount_paid).toLocaleString()}
                                            </td>
                                            <td>{formatToDayMonthYear(repayment.payment_date)}</td>
                                            <td>{repayment.received_by?.first_name } {repayment.received_by?.last_name }</td>
                                            <td>{formatTimestamp(repayment.created_at)}</td>
                                            <td>
                                                <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                    <div className="dropdown-trigger">
                                                        <button
                                                            className="button is-white"
                                                            aria-haspopup="true"
                                                            aria-controls="dropdown-menu"
                                                            onClick={() => toggleDropdown(index)}
                                                        >
                                                            <span className="icon">
                                                                <i className="fas fa-ellipsis-v"></i>
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                        <div className="dropdown-content">
                                                            <Link to={`${repayment.id}/details`} className="dropdown-item">
                                                                <span className="icon">
                                                                    <i className="fas fa-eye"></i>
                                                                </span>
                                                                <span>View Details</span>
                                                            </Link>
                                                            {repayment.status === 'completed' && canCancel(userRole) && (
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={() => handleCancelRepayment(repayment)}
                                                                >
                                                                    <span className="icon has-text-danger">
                                                                        <i className="fas fa-times"></i>
                                                                    </span>
                                                                    <span className="has-text-danger">Cancel</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="has-text-centered">
                                            No debt repayments found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}
            </main>
        </div>
    );
}

export default DebtRepayments;